import * as THREE from "three";

export async function createImageLayer(src, canvasW, canvasH, renderOrder, transparent = false) {
  if (!src) return { mesh: null, dispose: () => {} };

  const texture = await loadTexture(src, canvasW, canvasH);
  if (!texture) return { mesh: null, dispose: () => {} };

  const geo = new THREE.PlaneGeometry(canvasW, canvasH);
  const mat = new THREE.MeshBasicMaterial({
    map: texture,
    transparent,
    depthTest: false,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.renderOrder = renderOrder;

  return {
    mesh,
    dispose() {
      texture.dispose();
      geo.dispose();
      mat.dispose();
    },
  };
}

export async function updateImageLayerSrc(layerObj, src, canvasW, canvasH) {
  if (!layerObj || !layerObj.mesh) return;
  const oldTex = layerObj.mesh.material.map;
  const texture = await loadTexture(src, canvasW, canvasH);
  if (!texture) return;
  layerObj.mesh.material.map = texture;
  layerObj.mesh.material.needsUpdate = true;
  if (oldTex) oldTex.dispose();
}

async function loadTexture(src, canvasW, canvasH) {
  if (!src) return null;
  try {
    // Load image
    const img = await loadImage(src);
    const imgW = img.naturalWidth  || img.width;
    const imgH = img.naturalHeight || img.height;

    // Cover-crop math: same as CSS object-fit:cover centered
    const imgAspect    = imgW / imgH;
    const targetAspect = canvasW / canvasH;

    let sx, sy, sw, sh;
    if (imgAspect > targetAspect) {
      sh = imgH;
      sw = imgH * targetAspect;
      sx = (imgW - sw) / 2;
      sy = 0;
    } else {
      sw = imgW;
      sh = imgW / targetAspect;
      sx = 0;
      sy = (imgH - sh) / 2;
    }

    // Draw onto an offscreen canvas at the exact output resolution.
    // Canvas 2D always produces top-left origin — Three.js CanvasTexture
    // handles this correctly with its default flipY=true.
    const offscreen = document.createElement("canvas");
    offscreen.width  = canvasW;
    offscreen.height = canvasH;
    const ctx = offscreen.getContext("2d");
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvasW, canvasH);

    const tex = new THREE.CanvasTexture(offscreen);
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  } catch (e) {
    console.warn("ImageLayer: failed to load", src, e);
    return null;
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload  = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image: " + src));
    if (!src.startsWith("blob:") && !src.startsWith("data:")) {
      img.crossOrigin = "anonymous";
    }
    img.src = src;
  });
}
